import { useEffect, useMemo, useState } from 'react'
import { apiRequest } from '../api/client'
import { useAuth } from '../auth/AuthContext'

// Mirrors EmailTemplate#render in the Ruby model — `{{var}}` gets replaced;
// unknown vars stay as-is so typos are visible in preview.
function interpolate(template, vars) {
  return (template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    if (vars[key] !== undefined) return String(vars[key])
    return match
  })
}

function templateLabel(key) {
  return key
    .replace(/^trade_applications\./, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

export default function AdminEmailTemplatesPage() {
  const { user } = useAuth()
  const [templates, setTemplates] = useState([])
  const [sampleVars, setSampleVars] = useState({})
  const [selected, setSelected] = useState(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    apiRequest('/api/v1/admin/email_templates')
      .then(({ email_templates, sample_variables }) => {
        setTemplates(email_templates)
        setSampleVars(sample_variables || {})
        if (email_templates.length > 0) selectTemplate(email_templates[0])
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectTemplate = (t) => {
    setSelected(t)
    setSubject(t.subject)
    setBody(t.body)
    setNotice('')
    setError('')
  }

  const dirty = selected && (subject !== selected.subject || body !== selected.body)

  const previewSubject = useMemo(() => interpolate(subject, sampleVars), [subject, sampleVars])
  const previewBody    = useMemo(() => interpolate(body, sampleVars), [body, sampleVars])

  const save = async () => {
    if (!selected) return
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const { email_template } = await apiRequest(
        `/api/v1/admin/email_templates/${selected.id}`,
        { method: 'PATCH', body: { email_template: { subject, body } } }
      )
      setTemplates((all) => all.map((t) => (t.id === email_template.id ? email_template : t)))
      setSelected(email_template)
      setSubject(email_template.subject)
      setBody(email_template.body)
      setNotice('Saved')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const sendTest = async () => {
    if (!selected) return
    if (dirty) {
      setError('Save your changes before sending a test.')
      return
    }
    setSending(true)
    setError('')
    setNotice('')
    try {
      const { message } = await apiRequest(
        `/api/v1/admin/email_templates/${selected.id}/test_send`,
        { method: 'POST' }
      )
      setNotice(message || `Test email queued to ${user?.email}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return <p className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-slate-600">Loading…</p>
  }
  if (loadError) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{loadError}</div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Email templates</h1>
        <p className="text-sm text-slate-600 mt-1">
          Edit the templates customers receive. Preview is rendered with sample values for{' '}
          <code className="text-xs">{`{{variable}}`}</code> placeholders. Send a test to your own
          email to confirm the actual delivery.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Left: list */}
        <div className="card p-2">
          <ul className="space-y-1">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => selectTemplate(t)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                    selected?.id === t.id
                      ? 'bg-brand-50 text-brand-800 border border-brand-200'
                      : 'text-slate-700 hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <div className="font-medium">{templateLabel(t.template_key)}</div>
                  <div className="text-xs text-slate-500 truncate">{t.template_key}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: editor + preview */}
        {selected ? (
          <div className="space-y-4">
            <div className="card p-5 sm:p-6">
              <div className="flex items-baseline justify-between mb-4 gap-2 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{templateLabel(selected.template_key)}</h2>
                  <p className="text-xs text-slate-500 font-mono">{selected.template_key}</p>
                </div>
                <div className="text-xs text-slate-500">
                  {selected.updated_by_email && (
                    <>last edited by {selected.updated_by_email} ·{' '}
                      {new Date(selected.updated_at).toLocaleString()}
                    </>
                  )}
                </div>
              </div>

              {error && (
                <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
              )}
              {notice && (
                <div className="mb-3 p-2 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">{notice}</div>
              )}

              {(selected.variables || []).length > 0 && (
                <div className="mb-4 text-xs text-slate-600">
                  Available variables:{' '}
                  {(selected.variables || []).map((v, i) => (
                    <span key={v}>
                      <code className="font-mono px-1 py-0.5 rounded bg-slate-100">{`{{${v}}}`}</code>
                      {i < (selected.variables.length - 1) ? ' · ' : ''}
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="label" htmlFor="subject">Subject</label>
                  <input
                    id="subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label" htmlFor="body">Body</label>
                  <textarea
                    id="body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={10}
                    className="input font-mono text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <button onClick={save} disabled={!dirty || saving} className="btn-primary">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={sendTest}
                  disabled={sending || dirty}
                  title={dirty ? 'Save your changes first' : `Send to ${user?.email}`}
                  className="btn-secondary"
                >
                  {sending ? 'Sending…' : `Send test to ${user?.email}`}
                </button>
                {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
              </div>
            </div>

            <div className="card p-5 sm:p-6 bg-slate-50/40">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">
                Preview (with sample values)
              </h3>
              <div className="text-xs text-slate-500 mb-2">
                <strong>From:</strong> {user?.email || 'no-reply@brooklinen.com'} (or whatever MAILER_FROM_EMAIL resolves to)
              </div>
              <div className="text-sm text-slate-700 mb-2">
                <strong className="text-xs uppercase tracking-wider text-slate-500 mr-1">Subject</strong>
                <div className="font-medium text-slate-900">{previewSubject}</div>
              </div>
              <div className="text-sm text-slate-700">
                <strong className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Body</strong>
                <pre className="whitespace-pre-wrap font-sans bg-white border border-slate-200 rounded-md p-3 text-slate-800">
                  {previewBody}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="card p-8 text-center text-slate-500">
            Select a template to edit.
          </div>
        )}
      </div>
    </div>
  )
}
